import { Box, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { useGetProcessQuery } from '../../api/content.api';
import { AiToolRow } from '../../components/AiToolRow';
import { ContentSection } from '../../components/ContentSection';
import { IcomCards } from '../../components/IcomCards';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { MethodCard } from '../../components/MethodCard';
import { ProseSections } from '../../components/ProseSections';
import { ViewInConfluence } from '../../components/ViewInConfluence';
import { SECTION_COPY } from '../../content';
import { cardGridSx, sectionSpacingSx } from '../../theme';
import { ContentState } from '../../components/ContentState';

/**
 * `/sdlc/:phaseSlug/:processSlug` — one process and the methods under it.
 *
 * **The route parameters are rejoined into the record's `path`**, which is what
 * `GET /content/processes/{path+}` is keyed on. Sending the last segment alone
 * would 404 a record that exists.
 *
 * **This page needs no catalog.** Unlike the phase above it, its own request
 * returns the children it renders — so the method grid is one request, and the
 * 260KB catalog is left to the pages that genuinely need it.
 */
export const ProcessPage = () => {
  const { phaseSlug = '', processSlug = '' } = useParams();
  const path = `${phaseSlug}/${processSlug}`;
  const { data, isLoading, error, refetch } = useGetProcessQuery(path);

  const process = data?.process;
  const methods = data?.methods ?? [];

  return (
    <Box sx={{ paddingBottom: 8 }}>
      <Box sx={{ marginTop: 8 }}>
        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load this process"
          errorNoun="process"
          skeleton={<LoadingSkeleton layout="process" label="Loading the process" />}
        >
          {process !== undefined && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 4,
                  }}
                >
                  <Typography
                    variant="overline"
                    component="p"
                    sx={{ color: 'text.secondary', letterSpacing: '1px' }}
                  >
                    Level 2 &middot; Process
                  </Typography>

                  <ViewInConfluence confluencePageId={process.confluencePageId} />
                </Box>

                <Typography variant="h1" component="h1">
                  {/* The dotted reference is part of the title in the designs,
                      and the API returns the two halves separately. */}
                  {process.phaseNumber}.{process.processNumber} {process.process}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Owner: {process.content.ownerText}
                </Typography>

                <AiToolRow tools={process.content.aiToolList} />
              </Box>

              <Box sx={sectionSpacingSx}>
                <ProseSections content={process.content} />
              </Box>

              <Divider sx={sectionSpacingSx} />

              <Box sx={sectionSpacingSx}>
                <IcomCards content={process.content} />
              </Box>

              <Divider sx={sectionSpacingSx} />

              <Box sx={sectionSpacingSx}>
                <ContentSection
                  heading={SECTION_COPY.methods.heading}
                  caption={SECTION_COPY.methods.caption}
                >
                  {methods.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No methods are catalogued under this process yet.
                    </Typography>
                  ) : (
                    <Box
                      component="ul"
                      sx={{ ...cardGridSx('card'), listStyle: 'none', margin: 0, padding: 0 }}
                    >
                      {methods.map((method) => (
                        <Box component="li" key={method.id} sx={{ display: 'flex' }}>
                          <MethodCard
                            method={method}
                            reference={`${String(process.phaseNumber)}.${String(process.processNumber)}.${String(method.number)}`}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </ContentSection>
              </Box>
            </>
          )}
        </ContentState>
      </Box>
    </Box>
  );
};
